import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import PaginatedTable from "../components/library/Pagination";
import Loader from "../components/common/Loader";
import API_BASE_URL from "../config/api";

const CompanyEventsNewsPage = () => {
    const { token } = useContext(AuthContext);
    const employeeCode = sessionStorage.getItem("username");

    const [activeTab, setActiveTab] = useState("events");

    // Data
    const [events, setEvents] = useState([]);
    const [news, setNews] = useState([]);

    // Dialog states & edit IDs
    const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
    const [isNewsDialogOpen, setIsNewsDialogOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null); // object or null
    const [editingNews, setEditingNews] = useState(null);

    // Form states
    const [eventForm, setEventForm] = useState({ title: "", date: "", description: "" });
    const [newsForm, setNewsForm] = useState({ title: "", date: "", description: "" });

    const [notification, setNotification] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        fetchEvents();
        fetchNews();
        setLoading(false);
    }, []);

    const fetchEvents = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/Company/GetCompanyEvents`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setEvents(data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchNews = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/Company/GetCompanyNews`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setNews(data);
            }
        } catch (err) {
            console.error(err);
        }
    };


    const handleEventChange = (e) => {
        const { name, value } = e.target;

        if (value.length <= 500) {
            setEventForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleNewsChange = (e) => {
        const { name, value } = e.target;

        if (value.length <= 500) {
            setNewsForm((prev) => ({ ...prev, [name]: value }));
        }
    };


    // Validation  
    const validateEntry = (entry) => {
        if (!entry.title.trim() || !entry.eventDate.trim() || !entry.description.trim()) {
            return "All fields are required.";
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const entryDate = new Date(entry.eventDate);
        if (entryDate <= today) {
            return "Date must be in the future.";
        }

        return null; // valid
    };

    // Validation
    const validateNewsEntry = (entry) => {
        if (!entry.title.trim() || !entry.date.trim() || !entry.description.trim()) {
            return "All fields are required.";
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const entryDate = new Date(entry.date);
        if (entryDate <= today) {
            return "Date must be in the future.";
        }

        return null; // valid
    };

    // Open dialogs (Add or Edit)
    const openAddEventDialog = () => {
        closeNotification();
        setEditingEvent(null);
        setEventForm({ title: "", eventDate: "", description: "" });
        setIsEventDialogOpen(true);
    };

    const openEditEventDialog = (ev) => {
        closeNotification();
        setEditingEvent(ev);
        setEventForm({
            title: ev.title || "",
            eventDate: ev.eventDate ? ev.eventDate.split("T")[0] : "",
            description: ev.description || "",
        });
        setIsEventDialogOpen(true);
    };

    const openAddNewsDialog = () => {
        closeNotification();
        setEditingNews(null);
        setNewsForm({ title: "", date: "", description: "" });
        setIsNewsDialogOpen(true);
    };

    const openEditNewsDialog = (nw) => {
        closeNotification();
        setEditingNews(nw);
        setNewsForm({
            title: nw.title || "",
            date: nw.date ? nw.date.split("T")[0] : "",
            description: nw.description || "",
        });
        setIsNewsDialogOpen(true);
    };

    // Submit event (Add or Edit)
    const submitEvent = async () => {
        const error = validateNewsEntry(newsForm);
        if (error) {
            setNotification({ type: "error", message: error });
            return;
        }
        try {
            setLoading(true);
            eventForm.createdBy = employeeCode;
            const method = editingEvent ? "PUT" : "POST";
            const url = editingEvent
                ? `${API_BASE_URL}/api/company/UpdateCompanyEvents/${editingEvent.id}`
                : `${API_BASE_URL}/api/company/SetCompanyEvents`;

            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(eventForm),
            });
            if (res.ok) {
                setNotification({
                    type: "success",
                    message: editingEvent ? "Event updated successfully." : "Event added successfully.",
                });
                setIsEventDialogOpen(false);
                setEditingEvent(null);
                setEventForm({ title: "", date: "", description: "" });
                fetchEvents();
            } else {
                const error = await res.text();
                setNotification({ type: "error", message: "Error: " + error });
            }
        } catch (error) {
            setNotification({ type: "error", message: "Network error: " + error.message });
        } finally {
            setLoading(false);
        }
    };

    // Submit news (Add or Edit)
    const submitNews = async () => {

        const error = validateNewsEntry(newsForm);
        if (error) {
            setNotification({ type: "error", message: error });
            return;
        }

        try {
            setLoading(true);
            newsForm.createdBy = employeeCode;
            const method = editingNews ? "PUT" : "POST";
            const url = editingNews
                ? `${API_BASE_URL}/api/company/UpdateCompanyNews/${editingNews.id}`
                : `${API_BASE_URL}/api/company/SetCompanyNews`;

            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newsForm),
            });
            if (res.ok) {
                setNotification({
                    type: "success",
                    message: editingNews ? "News updated successfully." : "News added successfully.",
                });
                setIsNewsDialogOpen(false);
                setEditingNews(null);
                setNewsForm({ title: "", date: "", description: "" });
                fetchNews();
            } else {
                const error = await res.text();
                setNotification({ type: "error", message: "Error: " + error });
            }
        } catch (error) {
            setNotification({ type: "error", message: "Network error: " + error.message });
        } finally {
            setLoading(false);
        }
    };

    // Add these two functions inside your component:

    const deleteEvent = async (id) => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/company/DeleteCompanyEvent/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });

            if (res.ok) {
                setNotification({ type: "success", message: "Event deleted successfully." });
                fetchEvents();
            } else {
                const error = await res.text();
                setNotification({ type: "error", message: "Error deleting event: " + error });
            }
        } catch (error) {
            setNotification({ type: "error", message: "Network error: " + error.message });
        } finally {
            setLoading(false);
        }
    };

    const deleteNews = async (id) => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/company/DeleteCompanyNews/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });

            if (res.ok) {
                setNotification({ type: "success", message: "News deleted successfully." });
                fetchNews();
            } else {
                const error = await res.text();
                setNotification({ type: "error", message: "Error deleting news: " + error });
            }
        } catch (error) {
            setNotification({ type: "error", message: "Network error: " + error.message });
        } finally {
            setLoading(false);
        }
    };


    const closeNotification = () => setNotification(null);

    return (
        <>
            {loading ? (
                <Loader message="Loading..." />
            ) : (
                <div className="relative flex min-h-screen flex-col bg-white group/design-root overflow-x-hidden">
                    <div className="layout-container flex h-full grow flex-col">
                        <div className="flex flex-1 justify-center py-5">
                            <div className="layout-content-container flex flex-col flex-1">
                                <h1 className="text-3xl font-bold mb-6">Company Events & News</h1>

                                {/* {notification && (
                        <div
                            className={`mb-4 p-4 rounded ${notification.type === "error"
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                                }`}
                        >
                            <div className="flex justify-between items-center">
                                <span>{notification.message}</span>
                                <button onClick={closeNotification} className="font-bold text-xl leading-none">
                                    ×
                                </button>
                            </div>
                        </div>
                    )} */}

                                {/* Tabs */}
                                <div className="flex border-b mb-6">
                                    <button
                                        onClick={() => setActiveTab("events")}
                                        className={`px-6 py-3 font-semibold ${activeTab === "events"
                                            ? "border-b-2 border-black text-black"
                                            : "text-black"
                                            }`}
                                    >
                                        Events
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("news")}
                                        className={`px-6 py-3 font-semibold ${activeTab === "news"
                                            ? "border-b-2 border-black text-black"
                                            : "text-black"
                                            }`}
                                    >
                                        News
                                    </button>
                                </div>

                                {/* Events Tab */}
                                {activeTab === "events" && (
                                    <section>
                                        <div className="flex justify-between items-center mb-4">
                                            <h2 className="text-2xl font-semibold">Upcoming Events</h2>
                                            <button
                                                onClick={openAddEventDialog}
                                                className="custom-btn"
                                            >
                                                + Add Event
                                            </button>
                                        </div>
                                        <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
                                            <PaginatedTable
                                                columns={[
                                                    "Title",
                                                    "Date",
                                                    "Description",
                                                    "Actions",
                                                ]}
                                                data={events}
                                                pageSize={10}
                                                renderRow={(item, index) => (
                                                    <tr key={index} className="border-t border-t-[#dde1e3]">
                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                                                            {item.title}
                                                        </td>
                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                                                            {new Date(item.eventDate).toLocaleDateString()}
                                                        </td>

                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal" style={{ lineBreak: "anywhere" }}>
                                                            {item.description}
                                                        </td>

                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                                                            <div className="flex gap-3">
                                                                <button
                                                                    onClick={() => openEditEventDialog(item)}
                                                                    className="text-blue-600 underline"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => deleteEvent(item.id)}
                                                                    className="text-red-600 underline"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>

                                                    </tr>
                                                )}
                                            />
                                        </div>

                                        {/* Event Dialog */}
                                        {isEventDialogOpen && (
                                            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                                                <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
                                                    <h3 className="text-xl font-semibold mb-4">
                                                        {editingEvent ? "Edit Event" : "Add Event"}
                                                    </h3>

                                                    {notification && (
                                                        <div
                                                            className={`mb-4 p-4 rounded ${notification.type === "error"
                                                                ? "bg-red-100 text-red-700"
                                                                : "bg-green-100 text-green-700"
                                                                }`}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <span>{notification.message}</span>
                                                                <button onClick={closeNotification} className="font-bold text-xl leading-none">
                                                                    ×
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <input
                                                        type="text"
                                                        name="title"
                                                        placeholder="Title"
                                                        value={eventForm.title}
                                                        onChange={handleEventChange}
                                                        className="w-full border rounded p-2 mb-3"
                                                    />
                                                    <input
                                                        type="date"
                                                        name="eventDate"
                                                        value={eventForm.eventDate}
                                                        onChange={handleEventChange}
                                                        className="w-full border rounded p-2 mb-3"
                                                        min={new Date().toISOString().split("T")[0]}
                                                    />
                                                    <textarea
                                                        name="description"
                                                        placeholder="Description"
                                                        value={eventForm.description}
                                                        onChange={handleEventChange}
                                                        className="w-full border rounded p-2 mb-4"
                                                        rows={3}
                                                        maxLength={300}
                                                    />
                                                    <div className="flex justify-end gap-3">
                                                        <button
                                                            onClick={() => {
                                                                setIsEventDialogOpen(false);
                                                                setEditingEvent(null);
                                                            }}
                                                            className="cancel-btn"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={submitEvent}
                                                            className="btn-black"
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </section>
                                )}

                                {/* News Tab */}
                                {activeTab === "news" && (
                                    <section>
                                        <div className="flex justify-between items-center mb-4">
                                            <h2 className="text-2xl font-semibold">Latest News</h2>
                                            <button
                                                onClick={openAddNewsDialog}
                                                className="custom-btn"
                                            >
                                                + Add News
                                            </button>
                                        </div>
                                        <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
                                            <PaginatedTable
                                                columns={[
                                                    "Title",
                                                    "Date",
                                                    "Description",
                                                    "Actions",
                                                ]}
                                                data={news}
                                                pageSize={10}
                                                renderRow={(item, index) => (
                                                    <tr key={index} className="border-t border-t-[#dde1e3]">
                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                                                            {item.title}
                                                        </td>
                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                                                            {new Date(item.date).toLocaleDateString()}
                                                        </td>

                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal" style={{ lineBreak: "anywhere" }}>
                                                            {item.description}
                                                        </td>

                                                        <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                                                            <div className="flex gap-3">
                                                                <button
                                                                    onClick={() => openEditNewsDialog(item)}
                                                                    className="text-blue-600 underline"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => deleteNews(item.id)}
                                                                    className="text-red-600 underline"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>

                                                    </tr>
                                                )}
                                            />
                                        </div>

                                        {/* News Dialog */}
                                        {isNewsDialogOpen && (
                                            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                                                <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
                                                    <h3 className="text-xl font-semibold mb-4">
                                                        {editingNews ? "Edit News" : "Add News"}
                                                    </h3>

                                                    {notification && (
                                                        <div
                                                            className={`mb-4 p-4 rounded ${notification.type === "error"
                                                                ? "bg-red-100 text-red-700"
                                                                : "bg-green-100 text-green-700"
                                                                }`}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <span>{notification.message}</span>
                                                                <button onClick={closeNotification} className="font-bold text-xl leading-none">
                                                                    ×
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <input
                                                        type="text"
                                                        name="title"
                                                        placeholder="Title"
                                                        value={newsForm.title}
                                                        onChange={handleNewsChange}
                                                        className="w-full border rounded p-2 mb-3"
                                                    />
                                                    <input
                                                        type="date"
                                                        name="date"
                                                        value={newsForm.date}
                                                        onChange={handleNewsChange}
                                                        className="w-full border rounded p-2 mb-3"
                                                    />
                                                    <textarea
                                                        name="description"
                                                        placeholder="Description"
                                                        value={newsForm.description}
                                                        onChange={handleNewsChange}
                                                        className="w-full border rounded p-2 mb-4"
                                                        rows={3}
                                                        maxLength={300}
                                                    />
                                                    <div className="flex justify-end gap-3">
                                                        <button
                                                            onClick={() => {
                                                                setIsNewsDialogOpen(false);
                                                                setEditingNews(null);
                                                            }}
                                                            className="cancel-btn"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={submitNews}
                                                            className="btn-black"
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CompanyEventsNewsPage;
